import type { IFilesystem, FileMetadata } from '@livx.cc/wcli/core';
import { PathResolver } from '@livx.cc/wcli/core';
import { BodDB } from '@bod.ee/db';
import type { VFSBackend } from '@bod.ee/db';

/** Pure in-memory byte store for the default (no-arg) constructor — keeps it truly in-memory
 *  (bod-db's default backend writes bytes to disk under `storageRoot`). Keyed by fileId. */
class MemBackend implements VFSBackend {
  private store = new Map<string, Uint8Array>();
  async read(id: string): Promise<Uint8Array> {
    const d = this.store.get(id);
    if (!d) throw new Error(`fileId not found: ${id}`);
    return d;
  }
  async write(id: string, data: Uint8Array): Promise<void> { this.store.set(id, data); }
  async delete(id: string): Promise<void> { this.store.delete(id); }
  async exists(id: string): Promise<boolean> { return this.store.has(id); }
}

/**
 * Database-backed `IFilesystem` over bod-db's VFS — the normalization thesis realized: the agent
 * operates on a file tree that lives in a database, yet the same tools/commands run unchanged
 * (semantics mirror MemFilesystem/NodeDiskFilesystem: throw on missing parent / existing dir /
 * non-empty dir / missing file). bod-db's VFS is path-based and async, so the adapter is thin.
 *
 * Construct with no args for an in-memory DB (great for tests), or pass a configured `BodDB`
 * (with VFS enabled) backed by real/cloud storage for persistence. Call `close()` when done to
 * release the DB's timers (the in-memory default uses `sweepInterval: 0`, so there's nothing to sweep).
 */
export class BodDbFilesystem implements IFilesystem {
  private cwd = '/';
  private db: BodDB;
  private vfs: NonNullable<BodDB['vfs']>;
  private owns: boolean;

  constructor(db?: BodDB) {
    this.owns = !db;
    // default: metadata DB in-memory (path ':memory:'), file bytes in a Map backend → no disk, no sweep timer.
    this.db = db ?? new BodDB({ path: ':memory:', sweepInterval: 0, vfs: { storageRoot: ':memory:', backend: new MemBackend() } });
    if (!this.db.vfs) throw new Error('BodDbFilesystem: the BodDB must have VFS enabled (construct it with { vfs: { … } }).');
    this.vfs = this.db.vfs;
  }

  /** Release the underlying DB's timers/handles. Closes only a DB we created (a passed-in DB is the caller's). */
  close(): void {
    if (this.owns) this.db.close();
  }

  resolvePath(path: string, cwd?: string): string {
    return PathResolver.resolve(path, cwd || this.cwd);
  }
  getCwd(): string { return this.cwd; }
  setCwd(path: string): void { this.cwd = PathResolver.normalize(path); }

  /** Parent dir of an absolute VFS path ('/a/b.txt' -> '/a'; '/a' -> '/'). */
  private parentOf(p: string): string {
    const i = p.lastIndexOf('/');
    return i <= 0 ? '/' : p.slice(0, i);
  }
  /** Throw (matching the other backends) unless the parent directory exists. Root is always a dir. */
  private assertParentDir(p: string, path: string): void {
    const parent = this.parentOf(p);
    if (parent === '/') return;
    const s = this.vfs.stat(parent);
    if (!s || !s.isDir) throw new Error(`Parent directory does not exist: ${path}`);
  }

  async readFile(path: string): Promise<string> {
    const p = this.resolvePath(path);
    const s = this.vfs.stat(p);
    if (!s) throw new Error(`File not found: ${path}`);
    if (s.isDir) throw new Error(`Not a file: ${path}`);
    return new TextDecoder().decode(await this.vfs.read(p));
  }

  async writeFile(path: string, content: string): Promise<void> {
    const p = this.resolvePath(path);
    this.assertParentDir(p, path);
    if (this.vfs.stat(p)?.isDir) throw new Error(`Is a directory: ${path}`);
    await this.vfs.write(p, new TextEncoder().encode(content), 'text/plain');
  }

  async deleteFile(path: string): Promise<void> {
    const p = this.resolvePath(path);
    const s = this.vfs.stat(p);
    if (!s) throw new Error(`File not found: ${path}`);
    if (s.isDir && this.vfs.list(p).length > 0) throw new Error(`Directory not empty: ${path}`);
    await this.vfs.remove(p);
  }

  async readDir(path: string): Promise<string[]> {
    const p = this.resolvePath(path);
    if (p !== '/') {
      const s = this.vfs.stat(p);
      if (!s) throw new Error(`Directory not found: ${path}`); // missing vs…
      if (!s.isDir) throw new Error(`Not a directory: ${path}`); // …a file (match MemFilesystem semantics)
    }
    return this.vfs.list(p).map((e: { name: string }) => e.name);
  }

  async createDir(path: string): Promise<void> {
    const p = this.resolvePath(path);
    if (p === '/' || this.vfs.stat(p)) throw new Error(`File or directory already exists: ${path}`);
    this.assertParentDir(p, path);
    this.vfs.mkdir(p);
  }

  async exists(path: string): Promise<boolean> {
    const p = this.resolvePath(path);
    return p === '/' || this.vfs.stat(p) !== null;
  }

  async stat(path: string): Promise<FileMetadata> {
    const p = this.resolvePath(path);
    const s = this.vfs.stat(p);
    if (!s) {
      if (p === '/') return { created: new Date(0), modified: new Date(0), size: 0, permissions: 'drwxr-xr-x', isExecutable: false };
      throw new Error(`File not found: ${path}`);
    }
    const when = new Date(s.mtime); // bod-db tracks a single mtime; reuse for created+modified
    return {
      created: when,
      modified: when,
      size: s.size,
      permissions: s.isDir ? 'drwxr-xr-x' : '-rw-r--r--',
      isExecutable: false,
    };
  }

  async isDirectory(path: string): Promise<boolean> {
    const p = this.resolvePath(path);
    return p === '/' || this.vfs.stat(p)?.isDir === true;
  }
  async isFile(path: string): Promise<boolean> {
    const s = this.vfs.stat(this.resolvePath(path));
    return !!s && !s.isDir;
  }
}
